import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import jsPDF from 'jspdf';
import './MonthInvoices.css';
import { 
  FaCalendarAlt, 
  FaSearch, 
  FaTimes, 
  FaEuroSign, 
  FaShoppingCart, 
  FaFilePdf, 
  FaChevronDown, 
  FaChevronUp,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

const MonthInvoices = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [searchMonth, setSearchMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Utilisation de useCallback pour stabiliser la fonction calculateMonthlyData
  const calculateMonthlyData = useCallback((commandes) => {
    const months = {};

    commandes.forEach((commande) => {
      const orderDate = new Date(commande.date.toDate());
      const monthKey = `${orderDate.getMonth() + 1}/${orderDate.getFullYear()}`;

      if (!months[monthKey]) {
        months[monthKey] = {
          revenue: 0,
          dishes: 0,
          details: [],
        };
      }

      months[monthKey].revenue += commande.total;
      months[monthKey].dishes += commande.items.length;
      months[monthKey].details.push(commande);
    });

    // Convertir l'objet en tableau, ajouter des dates et trier globalement par mois
    return Object.entries(months)
      .map(([key, value]) => {
        const [month, year] = key.split('/').map(Number);
        const startDate = new Date(year, month - 1); // Date pour trier les mois
        return {
          month: key,
          startDate,
          ...value,
        };
      })
      .sort((a, b) => b.startDate - a.startDate); // Tri par date de début du mois
  }, []);

  useEffect(() => {
    const fetchMonthlyData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const commandesSnapshot = await getDocs(collection(db, 'total_commande'));
        const commandes = commandesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const months = calculateMonthlyData(commandes);
        setMonthlyData(months);
        setSuccessMessage('Données mensuelles chargées avec succès');
        
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Erreur lors du chargement des données mensuelles');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyData();
  }, [calculateMonthlyData]);

  // Gestion responsive du nombre d'éléments par page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setItemsPerPage(3);
      } else {
        setItemsPerPage(5);
      }
    };

    // Appeler une fois au montage
    handleResize();

    // Écouter les changements de taille
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDate = (date) => {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;
  };

  const generatePDF = (month) => {
    try {
      const pdf = new jsPDF();
      
      // En-tête
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.text('SKANEAT - Rapport Mensuel', 20, 20);
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Période : ${month.month}`, 20, 35);
      
      // Informations générales
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Résumé du mois', 20, 50);
      
      pdf.setFont(undefined, 'normal');
      pdf.text(`Revenus totaux : ${month.revenue.toFixed(2)} €`, 20, 65);
      pdf.text(`Nombre de commandes : ${month.details.length}`, 20, 75);
      pdf.text(`Nombre de plats vendus : ${month.dishes}`, 20, 85);
      
      // Détails des commandes
      pdf.setFont(undefined, 'bold');
      pdf.text('Détail des commandes', 20, 105);
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      
      let yPosition = 120;
      month.details
        .sort((a, b) => {
          const dateA = new Date(a.date.toDate());
          const dateB = new Date(b.date.toDate());
          return dateA - dateB;
        })
        .forEach((commande, index) => {
          if (yPosition > 280) {
            pdf.addPage();
            yPosition = 20;
          }
          
          const orderDate = new Date(commande.date.toDate());
          const formattedDate = formatDate(orderDate);
          const time = orderDate.toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
          
          pdf.text(`${index + 1}. ${formattedDate} à ${time}`, 20, yPosition);
          pdf.text(`   Montant: ${commande.total.toFixed(2)} €`, 25, yPosition + 8);
          pdf.text(`   Plats: ${commande.items.length}`, 25, yPosition + 16);
          
          yPosition += 30;
        });
      
      // Pied de page
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(`Page ${i} sur ${pageCount}`, 20, pdf.internal.pageSize.height - 10);
        pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 
                 pdf.internal.pageSize.width - 60, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save(`Rapport_Mois_${month.month.replace(/\//g, '_')}.pdf`);
      setSuccessMessage('PDF généré avec succès');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de la génération du PDF');
      console.error('Erreur PDF:', err);
    }
  };

  // Fonction de filtrage des mois par date
  const filterByMonth = (months) => {
    if (!searchMonth) return months;

    const regex = /^\d{2}\/\d{4}$/;
    if (!regex.test(searchMonth)) return [];

    const [month, year] = searchMonth.split('/').map(Number);

    return months.filter((monthData) => {
      const [monthNumber, yearNumber] = monthData.month.split('/').map(Number);
      return monthNumber === month && yearNumber === year;
    });
  };

  // Fonctions de pagination
  const filteredMonths = filterByMonth(monthlyData);
  const totalPages = Math.ceil(filteredMonths.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMonths = filteredMonths.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedMonth(null);
  };

  const handleSearchChange = (e) => {
    setSearchMonth(e.target.value);
    setCurrentPage(1);
  };

  const getMonthName = (monthKey) => {
    const [month, year] = monthKey.split('/').map(Number);
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  if (loading) {
    return (
      <div className="month-invoices-container">
        <div className="month-invoices-loading-container">
          <div className="month-invoices-loading-spinner"></div>
          <p className="month-invoices-loading-text">Chargement des factures mensuelles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="month-invoices-container">
      {/* Header */}
      <div className="month-invoices-header">
        <div className="month-invoices-title-section">
          <FaCalendarAlt className="month-invoices-title-icon" />
          <h1 className="month-invoices-title">Factures Mensuelles</h1>
        </div>
        <p className="month-invoices-subtitle">
          Consultez et exportez les rapports de ventes par mois
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="month-invoices-error-message">
          <FaExclamationTriangle className="month-invoices-error-icon" />
          <span>{error}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="month-invoices-success-message">
          <FaCheckCircle className="month-invoices-success-icon" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Section de recherche */}
      <div className="month-invoices-search-section">
        <div className="month-invoices-search-container">
          <FaSearch className="month-invoices-search-icon" />
          <input
            type="text"
            className="month-invoices-search-input"
            value={searchMonth}
            onChange={handleSearchChange}
            placeholder="Rechercher un mois (ex: 12/2025)"
          />
        </div>
      </div>

      {/* Statistiques globales */}
      {monthlyData.length > 0 && (
        <div className="month-invoices-stats-grid">
          <div className="month-invoices-stat-card">
            <div className="month-invoices-stat-icon">
              <FaCalendarAlt />
            </div>
            <div className="month-invoices-stat-content">
              <div className="month-invoices-stat-value">{monthlyData.length}</div>
              <div className="month-invoices-stat-label">Mois</div>
            </div>
          </div>
          
          <div className="month-invoices-stat-card">
            <div className="month-invoices-stat-icon">
              <FaEuroSign />
            </div>
            <div className="month-invoices-stat-content">
              <div className="month-invoices-stat-value">
                {monthlyData.reduce((sum, month) => sum + month.revenue, 0).toFixed(2)}€
              </div>
              <div className="month-invoices-stat-label">Revenus totaux</div>
            </div>
          </div>
          
          <div className="month-invoices-stat-card">
            <div className="month-invoices-stat-icon">
              <FaShoppingCart />
            </div>
            <div className="month-invoices-stat-content">
              <div className="month-invoices-stat-value">
                {monthlyData.reduce((sum, month) => sum + month.details.length, 0)}
              </div>
              <div className="month-invoices-stat-label">Commandes totales</div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des mois */}
      <div className="month-invoices-months-section">
        <h2 className="month-invoices-section-title">
          <FaCalendarAlt className="month-invoices-section-icon" />
          Mois disponibles
        </h2>
        
        {filteredMonths.length === 0 ? (
          <div className="month-invoices-empty-state">
            <FaCalendarAlt className="month-invoices-empty-icon" />
            <p className="month-invoices-empty-text">
              {searchMonth ? 'Aucun mois trouvé pour cette période' : 'Aucune donnée disponible'}
            </p>
          </div>
        ) : (
          <>
            <div className="month-invoices-months-grid">
              {currentMonths.map((month, index) => (
                <div key={index} className="month-invoices-month-card">
                  <div className="month-invoices-month-header">
                    <div className="month-invoices-month-info">
                      <h3 className="month-invoices-month-title">{getMonthName(month.month)}</h3>
                      <div className="month-invoices-month-stats">
                        <span className="month-invoices-month-revenue">
                          <FaEuroSign /> {month.revenue.toFixed(2)}€
                        </span>
                        <span className="month-invoices-month-orders">
                          <FaShoppingCart /> {month.details.length} commandes
                        </span>
                      </div>
                    </div>
                    <button
                      className="month-invoices-expand-btn"
                      onClick={() => setExpandedMonth(expandedMonth === index ? null : index)}
                    >
                      {expandedMonth === index ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  </div>
                  
                  {expandedMonth === index && (
                    <>
                      <div className="month-invoices-modal-overlay" onClick={() => setExpandedMonth(null)} />
                      <div className="month-invoices-modal">
                        <div className="month-invoices-modal-header">
                          <h2 className="month-invoices-modal-title">
                            <FaCalendarAlt className="month-invoices-modal-icon" />
                            Détails de {getMonthName(month.month)}
                          </h2>
                          <button 
                            className="month-invoices-close-btn"
                            onClick={() => setExpandedMonth(null)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                        
                        <div className="month-invoices-modal-content">
                          <div className="month-invoices-modal-stats">
                            <div className="month-invoices-modal-stat">
                              <FaEuroSign className="month-invoices-modal-stat-icon" />
                              <div className="month-invoices-modal-stat-content">
                                <div className="month-invoices-modal-stat-value">{month.revenue.toFixed(2)}€</div>
                                <div className="month-invoices-modal-stat-label">Revenus totaux</div>
                              </div>
                            </div>
                            
                            <div className="month-invoices-modal-stat">
                              <FaShoppingCart className="month-invoices-modal-stat-icon" />
                              <div className="month-invoices-modal-stat-content">
                                <div className="month-invoices-modal-stat-value">{month.details.length}</div>
                                <div className="month-invoices-modal-stat-label">Commandes</div>
                              </div>
                            </div>
                            
                            <div className="month-invoices-modal-stat">
                              <FaShoppingCart className="month-invoices-modal-stat-icon" />
                              <div className="month-invoices-modal-stat-content">
                                <div className="month-invoices-modal-stat-value">{month.dishes}</div>
                                <div className="month-invoices-modal-stat-label">Plats vendus</div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="month-invoices-modal-orders">
                            <h3 className="month-invoices-modal-orders-title">Détail des commandes</h3>
                            <div className="month-invoices-modal-orders-list">
                              {month.details
                                .sort((a, b) => {
                                  const dateA = new Date(a.date.toDate());
                                  const dateB = new Date(b.date.toDate());
                                  return dateA - dateB;
                                })
                                .map((commande, idx) => {
                                  const orderDate = new Date(commande.date.toDate());
                                  const formattedDate = formatDate(orderDate);
                                  const time = orderDate.toLocaleTimeString('fr-FR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  });
                                  
                                  return (
                                    <div key={idx} className="month-invoices-order-item">
                                      <div className="month-invoices-order-date">
                                        {formattedDate} à {time}
                                      </div>
                                      <div className="month-invoices-order-details">
                                        <span className="month-invoices-order-amount">
                                          {commande.total.toFixed(2)}€
                                        </span>
                                        <span className="month-invoices-order-plates">
                                          {commande.items.length} plats
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                          
                          <div className="month-invoices-modal-actions">
                            <button 
                              className="month-invoices-download-btn"
                              onClick={() => generatePDF(month)}
                            >
                              <FaFilePdf className="month-invoices-download-icon" />
                              Télécharger le PDF
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="month-invoices-pagination">
                <div className="month-invoices-pagination-info">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, filteredMonths.length)} sur {filteredMonths.length} mois
                </div>
                <div className="month-invoices-pagination-controls">
                  <button
                    className="month-invoices-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  
                  <div className="month-invoices-pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`month-invoices-pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    className="month-invoices-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MonthInvoices;
